/* eslint-disable prettier/prettier */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { ClientInfo, DocumentRequest } from './types';
import { auditService } from './audit-service';

interface AuditContextType {
  // Selected client
  selectedClient: ClientInfo | null;
  setSelectedClient: (client: ClientInfo | null) => void;
  
  // Pending and sent documents
  pendingDocuments: DocumentRequest[];
  setPendingDocuments: (docs: DocumentRequest[]) => void;
  
  sentDocuments: DocumentRequest[];
  setSentDocuments: (docs: DocumentRequest[]) => void;
  
  // Conversation state
  conversationStage: 'identification' | 'document_listing' | 'response' | 'finished';
  setConversationStage: (stage: 'identification' | 'document_listing' | 'response' | 'finished') => void;
  
  // System messages
  systemMessages: {id: string; type: string; message: string; details?: string; timestamp: Date}[];
  addSystemMessage: (type: string, message: string, details?: string) => void;
  
  // Refresh documents
  refreshClientDocuments: (clientId: string) => void;
}

const AuditContext = createContext<AuditContextType | undefined>(undefined);

export const AuditProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Basic state
  const [selectedClient, setSelectedClient] = useState<ClientInfo | null>(null);
  const [pendingDocuments, setPendingDocuments] = useState<DocumentRequest[]>([]);
  const [sentDocuments, setSentDocuments] = useState<DocumentRequest[]>([]);
  
  // Conversation state
  const [conversationStage, setConversationStage] = useState<
    'identification' | 'document_listing' | 'response' | 'finished'
  >('identification');
  
  // System messages
  const [systemMessages, setSystemMessages] = useState<
    {id: string; type: string; message: string; details?: string; timestamp: Date}[]
  >([]);
  
  // Function to add system messages
  const addSystemMessage = (type: string, message: string, details?: string) => {
    setSystemMessages(prev => [
      {
        id: Math.random().toString(36).substring(2, 11),
        type,
        message,
        details,
        timestamp: new Date()
      },
      ...prev
    ]);
  };

  // Function to refresh documents for a client
  const refreshClientDocuments = (clientId: string) => {
    if (!clientId) return;
    
    try {
      setPendingDocuments(auditService.getPendingDocuments(clientId));
      setSentDocuments(auditService.getSentDocuments(clientId));
    } catch (error) {
      console.error('Error refreshing client documents:', error);
      addSystemMessage('error', 'Error al actualizar los documentos del cliente');
    }
  };

  // Update documents when selected client changes
  useEffect(() => {
    if (selectedClient) {
      refreshClientDocuments(selectedClient.id);
    }
  }, [selectedClient]);

  const value = {
    selectedClient,
    setSelectedClient,
    pendingDocuments,
    setPendingDocuments,
    sentDocuments,
    setSentDocuments,
    conversationStage,
    setConversationStage,
    systemMessages,
    addSystemMessage,
    refreshClientDocuments
  };

  return (
    <AuditContext.Provider value={value}>
      {children}
    </AuditContext.Provider>
  );
};

export const useAudit = () => {
  const context = useContext(AuditContext);
  if (context === undefined) {
    throw new Error('useAudit must be used within an AuditProvider');
  }
  return context;
};
